import {useDispatch, useSelector} from 'react-redux';
import {get} from 'lodash';

import {useClearMyTeamsFilters} from '/component/screen/report/MyTeamsReport/hooks/useClearMyTeamsFilters';
import {MetricsSection} from '/constant/TestIDs';
import {BUSINESS_LEADER} from '/enum/competency.enum';
import {
  MY_TEAMS,
  REPORT_ALL_COACHES_IN_ORGANIZATION,
  REPORT_ALL_FRONTLINE_COACHES,
  REPORT_ALL_GEN0_COACHES,
  REPORT_FRONTLINE_COACHES,
  REPORT_NEW_COACHES_IN_ORGANIZATION,
  REPORT_NEW_FRONTLINE_COACHES,
  REPORT_NEW_GEN0_COACHES,
} from '/enum/records.enum';
import {SCREEN_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {updateMyTeamsFilter} from '/redux/action/myTeams.action';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getDefaultNewCoachesKey} from '/util/filter.util';

import {getDefaultFilters} from '../../report/MyTeamsReport/util/myTeamsFilterDomain.util';

import {CoachesSectionProps, ListRecordProps} from './CoachesSection.types';

export const useCoachesSection = ({
  allFrontline = 0,
  allGen0 = 0,
  allOrganization = 0,
  myTeams = 0,
  currentUserCompetency = '',
  newFrontline = 0,
  newGen0 = 0,
  newOrganization = 0,
  onShowReport = () => {},
  onShowInfoPopover = () => {},
}: CoachesSectionProps) => {
  const featureFlagTeamOrg = useSelector(variablesFeatureFlag);
  const showTeamOrgFields = shouldReleaseFeature(
    get(featureFlagTeamOrg, 'ff_my_teams_org', ''),
  );

  const {tt, ts} = useTranslation('screen.teams.section.coachesSection');
  const dispatch = useDispatch();
  const defaultNewCoachesKey = getDefaultNewCoachesKey(true);
  const clearMyTeamsFilters = useClearMyTeamsFilters();
  const onInfoPress = (payload?: any) => {
    const popoverPayload = {
      heading: tt('info.heading', 'Coaches'),
      body: tt(
        'info.body',
        !showTeamOrgFields
          ? 'View frontline Coaches (those who are personally sponsored by you), Generation Zero (Gen 0) Coaches (those sponsored by you or a Coach on your team down to the first ED or higher), or in your organization (all Coaches on your team).'
          : 'View your team with filtering capabilities (All coaches in your organization), New Coaches in your Organization and New Coaches in your frontline',
      ),
      ...payload,
    };

    onShowInfoPopover?.(popoverPayload);
  };

  const onItemPress = ({recordsName, value, newValue, reportHeading}: any) => {
    if (reportHeading === 'New Frontline Coaches') {
      dispatch(
        updateMyTeamsFilter({
          ...getDefaultFilters(defaultNewCoachesKey),
          frontlineOnly: true,
        }),
      );
    } else {
      clearMyTeamsFilters(defaultNewCoachesKey);
    }

    onShowReport({
      recordsName,
      reportHeading,
      loadingData: {total: value, newTotal: newValue},
    });
  };
  const listItems: ListRecordProps[] = [];

  if (!showTeamOrgFields) {
    listItems.push({
      label: tt('frontline', 'Frontline Coaches'),
      value: allFrontline,
      recordsName: REPORT_FRONTLINE_COACHES,
      newValue: newFrontline,
      testID: MetricsSection.metricFrontlineCoaches,
    });
    listItems.push({
      label: tt('newFrontline', 'New Frontline Coaches'),
      value: newFrontline,
      recordsName: REPORT_NEW_FRONTLINE_COACHES,
      testID: MetricsSection.metricNewFrontlineCoaches,
    });
    listItems.push({
      label: tt('allFrontline', 'All Frontline Coaches'),
      value: allFrontline,
      recordsName: REPORT_ALL_FRONTLINE_COACHES,
      testID: MetricsSection.metricAllFrontlineCoaches,
    });
    listItems.push({
      label: tt('newGen0', 'New Generation 0 Coaches'),
      value: newGen0,
      recordsName: REPORT_NEW_GEN0_COACHES,
      testID: MetricsSection.metricNewGeneration0Coaches,
    });
    listItems.push({
      label: tt('allGen0', 'All Generation 0 Coaches'),
      value: allGen0,
      recordsName: REPORT_ALL_GEN0_COACHES,
      testID: MetricsSection.metricAllGeneration0Coaches,
    });

    if (currentUserCompetency === BUSINESS_LEADER) {
      listItems.push({
        label: tt('newOrganization', 'New Organization Coaches'),
        value: newOrganization,
        recordsName: REPORT_NEW_COACHES_IN_ORGANIZATION,
        testID: MetricsSection.metricNewOrganizationCoaches,
      });
      listItems.push({
        label: tt('allOrganization', 'All Organization Coaches'),
        value: allOrganization,
        recordsName: REPORT_ALL_COACHES_IN_ORGANIZATION,
        testID: MetricsSection.metricAllOrganizationCoaches,
      });
    }
  } else {
    listItems.push({
      label: tt('allOrganization', 'My Teams'),
      value: myTeams,
      recordsName: MY_TEAMS,
      reportHeading: 'My Teams',
      testID: MetricsSection.metricAllOrganizationCoaches,
      screenName: SCREEN_NAMES.myTeamsReport,
    });
    listItems.push({
      label: tt('newOrganization', 'New Organization Coaches'),
      value: newOrganization,
      recordsName: MY_TEAMS,
      reportHeading: 'New Organization Coaches',
      testID: MetricsSection.metricNewOrganizationCoaches,
    });
    listItems.push({
      label: tt('newFrontline', 'New Frontline Coaches'),
      value: newFrontline,
      recordsName: MY_TEAMS,
      reportHeading: 'New Frontline Coaches',
      testID: MetricsSection.metricNewFrontlineCoaches,
      screenName: 'new_frontline_coaches',
    });
  }

  return {
    listItems,
    tt,
    ts,
    onInfoPress,
    onItemPress,
  };
};
